import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

async function parseError(response, fallback) {
  const text = await response.text();
  if (!text) return fallback;
  try {
    const json = JSON.parse(text);
    return json.message || json.title || json.detail || text;
  } catch {
    return text;
  }
}

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(await parseError(response, `Request failed (${response.status})`));
  }

  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return response.json();
}

const authJson = (method, body) => ({ method, body, auth: true });

export const fetchCategoryTypes = () => request("/CategoryTypes/GetAll");
export const fetchCategoryTypeById = (id) => request(`/CategoryTypes/Get/${id}`);
export const createCategoryType = (payload) => request("/CategoryTypes/Add", authJson("POST", payload));
export const updateCategoryType = (id, payload) => request(`/CategoryTypes/Edit/${id}`, authJson("PUT", payload));
export const deleteCategoryType = (id) => request(`/CategoryTypes/Delete/${id}`, { method: "DELETE", auth: true });

export const fetchColors = () => request("/Colors/GetAll");
export const fetchColorById = (id) => request(`/Colors/Get/${id}`);
export const createColor = (payload) => request("/Colors/Add", authJson("POST", payload));
export const updateColor = (id, payload) => request(`/Colors/Edit/${id}`, authJson("PUT", payload));
export const deleteColor = (id) => request(`/Colors/Delete/${id}`, { method: "DELETE", auth: true });

export const fetchCaseStyles = () => request("/CaseStyles/GetAll");
export const fetchCaseStyleById = (id) => request(`/CaseStyles/Get/${id}`);
export const createCaseStyle = (payload) => request("/CaseStyles/Add", authJson("POST", payload));
export const updateCaseStyle = (id, payload) => request(`/CaseStyles/Edit/${id}`, authJson("PUT", payload));
export const deleteCaseStyle = (id) => request(`/CaseStyles/Delete/${id}`, { method: "DELETE", auth: true });

export const fetchProducts = (params = {}) => {
  const query = new URLSearchParams();
  if (params.categoryTypeId) query.set("categoryTypeId", String(params.categoryTypeId));
  if (params.categoryId) query.set("categoryId", String(params.categoryId));
  const suffix = query.toString() ? `?${query}` : "";
  return request(`/Products/GetAll${suffix}`);
};

export const fetchProductById = (id) => request(`/Products/Get/${id}`);

export const filterProducts = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const suffix = query.toString() ? `?${query}` : "";
  return request(`/Products/Filter${suffix}`);
};

export const createProduct = (payload) => request("/Products/Add", authJson("POST", payload));
export const updateProduct = (id, payload) => request(`/Products/Edit/${id}`, authJson("PUT", payload));
export const deleteProduct = (id) => request(`/Products/Delete/${id}`, { method: "DELETE", auth: true });

export const fetchProductVariants = (productId) => request(`/Products/Get/${productId}/Variants`);
export const fetchProductVariantById = (id) => request(`/Products/GetVariant/${id}`);
export const createProductVariant = (payload) => request("/Products/AddVariant", authJson("POST", payload));
export const updateProductVariant = (id, payload) => request(`/Products/EditVariant/${id}`, authJson("PUT", payload));
export const deleteProductVariant = (id) => request(`/Products/DeleteVariant/${id}`, { method: "DELETE", auth: true });

export const fetchRelatedItems = (ownerKind, ownerId) =>
  request(`/CatalogRelations?ownerKind=${encodeURIComponent(ownerKind)}&ownerId=${ownerId}`);

export const searchCatalogRelations = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  return request(`/CatalogRelations/search?${query}`);
};

export const fetchRelationCategories = (kind) =>
  request(`/CatalogRelations/categories?kind=${encodeURIComponent(kind)}`);

export const createCatalogRelation = (payload) =>
  request("/CatalogRelations", authJson("POST", payload));

export const updateCatalogRelation = (id, payload) =>
  request(`/CatalogRelations/${id}`, authJson("PUT", payload));

export const deleteCatalogRelation = (id) =>
  request(`/CatalogRelations/${id}`, { method: "DELETE", auth: true });

export const fetchCatalogCategories = async (categoryTypeId) => {
  const query = categoryTypeId ? `?categoryTypeId=${categoryTypeId}` : "";
  const data = await request(`/Category/GetAllCategories${query}`);
  return (data || []).map((category) => ({
    categoryId: category.categoryId,
    name: category.name,
    imageUrl: category.imageUrl,
    categoryTypeId: category.categoryTypeId,
    categoryTypeName: category.categoryTypeName,
  }));
};
